import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    clearAppointmentsAndSlotsByUserAndDateRange,
    clearAppointmentsCreated,
    clearUserEventsByUserAndDateRange,
    clearUserEventsCreated,
    searchAppointmentsAndSlotsByUserAndDateRange,
    searchUserEventsByUserAndDateRange,
    setCurrentUserEvent,
    setOpenAppointmentSchedulerPanel,
    setUsersCurrentAppointment,
    updateUserCalendarAppointment,
    updateUserCalendarAppointmentSlot,
    updateUserEvent
} from '../../../store/calendarService';
import {
    clearCourseModulesByUserAndDateRange,
    searchCourseModulesByUserAndDateRange
} from '../../../store/courseService';
import {
    loadAppointmentTypeDetails,
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import {
    clearParticipantsSearch,
    searchParticipants,
    setCurrentlySelectedParticipant
} from '../../../store/participantService';
import { clearUsersBySearch } from '../../../store/userService';
import { todayDate } from '../../../utils/dateFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { hasRole } from '../../../utils/userRoles';
import Calendar from '../calendar/Calendar';
import AdviserSelect from '../components/AdviserSelect';
import {
    acceptedRoles,
    configCourseEventContent,
    configParticipantAndCommsEventContent,
    configUserEventContent,
    fetchUserServiceIds,
    formatFullCalendarTimes,
    getBusinessHours,
    getServiceIds,
    group2,
    group3,
    mapColors,
    mapCommonParams,
    mapUserEventColors
} from '../utils/calendarUtils';

import UserAppointmentCreate from './UserAppointmentCreate';
import UserAppointmentEdit from './UserAppointmentEdit';
import UserAppointmentSlotsEdit from './UserAppointmentSlotsEdit';
import UserAppointmentSlotsView from './UserAppointmentSlotsView';
import UserAppointmentView from './UserAppointmentView';
import UserEventEdit from './UserEventEdit';

import classes from '../../../app.module.css';

const _DOUBLE_CLICK = 300;
let userClickCount = 0;

const UserCalendar = () => {
    //HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [range, setRange] = useState({
        dateFrom: todayDate(),
        dateTo: todayDate()
    });
    const [events, setEvents] = useState([]);
    const [userEvents, setUserEvents] = useState([]);
    const [courseEvents, setCourseEvents] = useState([]);
    const [newEvent, setNewEvent] = useState({});
    const [currentEvent, setCurrentEvent] = useState({});
    const [loggedInAdviser, setLoggedInAdviser] = useState({});
    const [adviser, setAdviser] = useState({});
    const [eventOwnerId, setEventOwnerId] = useState(''); // may not be adviser

    const [currentModules, setCurrentModules] = useState([]);
    const [currentUserEvents, setCurrentUserEvents] = useState([]);
    const [currentAppointments, setCurrentAppointments] = useState([]);

    const [currentParticipantEntry, setCurrentParticipantEntry] = useState({});
    const [participants, setParticipants] = useState({});
    const [participantIdsToSearch, setParticipantIdsToSearch] = useState([]);
    const [loggedInUsersServiceIds, setLoggedInUsersServiceIds] = useState([]);

    const [isPTRequestSent, setIsPTRequestSent] = useState(false);
    const [navLink, setNavLink] = useState('');

    const [viewOnlyAppointment, setViewOnlyAppointment] = useState(true);
    const [viewOnlySlots, setViewOnlySlots] = useState(true);
    const [canEditAppointment, setCanEditAppointment] = useState(false);
    const [canEditSlots, setCanEditSlots] = useState(false);
    const [canEditUserEvent, setCanEditUserEvent] = useState(false);
    const [canCreate, setCanCreate] = useState(false);
    const [isActionsDisabled, setIsActionsDisabled] = useState(true);
    const [restrictOnClosed, setRestrictOnClosed] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const usersByLoggedInUsersServiceIds = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIds
    );
    const users = useSelector((state) => state.entities.userService.usersBySearch);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );
    const appointmentTypes = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );

    // appointments
    const appointments = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateRange
    );
    const appointmentsMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateRangeMetaData
    );
    const loadingAppointments = useSelector(
        (state) => state.entities.calendarService.loadingUsersAppointmentsAndSlots
    );
    const appointmentsCreated = useSelector(
        (state) => state.entities.calendarService.appointmentsCreated
    );

    // user events
    const userEventsByUser = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateRange
    );
    const userEventsMetaData = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateRangeMetaData
    );
    const loadingUserEvents = useSelector(
        (state) => state.entities.calendarService.loadingUserEventsByUserAndDateRange
    );
    const userEventsCreated = useSelector(
        (state) => state.entities.calendarService.userEventsCreated
    );

    // course modules
    const courseModules = useSelector(
        (state) => state.entities.courseService.courseModulesByUserAndDateRange
    );
    const courseModulesMetaData = useSelector(
        (state) => state.entities.courseService.courseModulesByUserAndDateRangeMetaData
    );
    const loadingModules = useSelector(
        (state) => state.entities.courseService.loadingCourseModulesByUserAndDateRange
    );

    // PT search
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );

    // current
    const usersCurrentAppointment = useSelector(
        (state) => state.entities.calendarService.usersCurrentAppointment
    );
    const usersCurrentEvent = useSelector(
        (state) => state.entities.calendarService.usersCurrentEvent
    );

    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );

    // CONFIG FNS
    const configEvents = () => {
        if (!Array.isArray(currentAppointments)) return;
        const businessHours = getBusinessHours();
        let entries;
        const newCreatedAppointments =
            adviser.id in appointmentsCreated ? appointmentsCreated[adviser.id] : [];
        const newAppointments = [...currentAppointments, ...newCreatedAppointments];
        if (newAppointments.length < 1) entries = [{}];
        else
            entries = newAppointments.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { participantEntry, participantName, slots, filledSlots, displayCommLinks } =
                    configParticipantAndCommsEventContent(el, participants);
                const { color, textColor } = mapColors(el.id, usersCurrentAppointment?.id);
                const editable = el.id === usersCurrentAppointment?.id;
                const isEventUserRestricted =
                    participantEntry?.noMatchingService ||
                    !loggedInUsersServiceIds.includes(participantEntry?.serviceId);
                return {
                    id: el.id,
                    eventType: 'participant',
                    hasLink: true,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    slots,
                    filledSlots,
                    location: el.location,
                    participantEntry,
                    participant: participantName,
                    appointmentType: el.type,
                    additionalDetails: el.additionalDetails,
                    color,
                    textColor,
                    date,
                    start,
                    end,
                    editable,
                    displayCommLinks,
                    disableCommsEmail: !participantEntry?.emailAddress,
                    isEventUserRestricted,
                    constraint: businessHours
                };
            });
        setEvents(entries);
        const cur = entries.find((el) => el.id === usersCurrentAppointment?.id);
        if (cur) setCurrentEvent(cur);
    };

    const configUserEvents = () => {
        if (!Array.isArray(currentUserEvents)) return;
        let events;
        const newCreatedUserEvents =
            adviser.id in userEventsCreated ? userEventsCreated[adviser.id] : [];
        const newUserEvents = [...currentUserEvents, ...newCreatedUserEvents];
        if (newUserEvents.length < 1) events = [];
        else
            events = newUserEvents.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { color, textColor } = mapUserEventColors(el.id, usersCurrentEvent?.id);
                const { displayOccurrence, content } = configUserEventContent(el);
                const editable = el.id === usersCurrentEvent?.id && adviser.id === loggedInUser.id;
                const className =
                    adviser.id === loggedInUser.id ? 'user-events' : 'non-interactive-events';
                return {
                    id: el.id,
                    eventType: 'userEvent',
                    className,
                    content,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    color,
                    textColor,
                    date,
                    start,
                    end,
                    editable,
                    repeat: el.repeat,
                    repeatUntilDate: el.repeatUntilDate,
                    displayOccurrence
                };
            });
        setUserEvents(events);
        const cur = events.find((el) => el.id === usersCurrentEvent?.id);
        if (cur) setCurrentEvent(cur);
    };

    const configBackgroundEvents = () => {
        if (!Array.isArray(currentModules)) return;
        let events;
        if (!currentModules?.length) events = [];
        else
            events = currentModules.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const content = configCourseEventContent(el);
                return {
                    eventType: 'courseModule',
                    className: 'non-interactive-events',
                    content,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    date,
                    start,
                    end,
                    editable: false
                };
            });
        setCourseEvents(events);
    };

    // USE EFFECTS
    useEffect(() => {
        !appointmentTypes?.length && dispatch(loadAppointmentTypeDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!loggedInUser || !loggedInUser?.id || !usersByLoggedInUsersServiceIds) return;
        const user =
            usersByLoggedInUsersServiceIds.find((el) => el.id === loggedInUser.id) || loggedInUser;
        setAdviser(user);
        setLoggedInAdviser(user);
        const serviceIds = fetchUserServiceIds(loggedInUser, dispatch);
        setLoggedInUsersServiceIds(serviceIds);
    }, [loggedInUser, usersByLoggedInUsersServiceIds]);

    useEffect(() => {
        setViewOnlyAppointment(
            (hasRole(group2, roles) && restrictOnClosed) ||
                (hasRole(group3, roles) && !hasRole(acceptedRoles, roles))
        );
        setViewOnlySlots(hasRole(group3, roles) && !hasRole(acceptedRoles, roles));
        setCanEditSlots(hasRole(acceptedRoles, roles));
        setCanEditAppointment(hasRole(acceptedRoles, roles) && !restrictOnClosed);
        setCanCreate(hasRole(acceptedRoles, roles));
        setCanEditUserEvent(hasRole(acceptedRoles, roles));
        setIsActionsDisabled(!hasRole(acceptedRoles, roles));
    }, [roles?.length, restrictOnClosed]);

    useEffect(() => {
        if (!('id' in adviser)) return;
        fetchNewData(adviser.id);
    }, [adviser?.id, range]);

    useEffect(() => {
        if (
            !loadingAppointments &&
            !!appointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in appointmentsMetaData &&
            !appointmentsMetaData.last
        ) {
            dispatch(
                searchAppointmentsAndSlotsByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    appointmentsMetaData.number + 1
                )
            );
        } else if (appointmentsMetaData?.last) {
            if (!appointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length)
                configAppointments();
            else {
                let ptIds = [
                    ...new Set(
                        appointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]
                            .map((el) => el.participantId ?? el.participantIds)
                            .flat()
                    )
                ];
                ptIds = ptIds.filter((el) => el && !(el in participants));
                if (ptIds.length > 0) {
                    setParticipantIdsToSearch(ptIds);
                    dispatch(searchParticipants({ ids: ptIds }, 0));
                } else configAppointments();
            }
        }
    }, [
        appointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        loadingAppointments,
        appointmentsMetaData
    ]);

    useEffect(() => {
        if (!participantsSearch?.length) return;
        if (
            !!participantIdsToSearch?.length &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData &&
            !participantsSearchMetaData.last
        ) {
            dispatch(
                searchParticipants(
                    { ids: participantIdsToSearch },
                    participantsSearchMetaData.number + 1
                )
            );
        } else if (participantsSearchMetaData?.last) {
            const formattedParticipantSearch = participantsSearch.reduce(
                (acc, cur) => ({ ...acc, [cur.id]: cur }),
                {}
            );
            setParticipants((prev) => ({ ...prev, ...formattedParticipantSearch }));
            setParticipantIdsToSearch([]);
            configAppointments();
        }
    }, [participantsSearch, participantsSearchMetaData, loadingParticipantsSearch]);

    useEffect(() => {
        if (
            !loadingUserEvents &&
            !!userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in userEventsMetaData &&
            !userEventsMetaData.last
        ) {
            dispatch(
                searchUserEventsByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    userEventsMetaData.number + 1
                )
            );
        } else if (userEventsMetaData?.last) {
            setCurrentUserEvents(
                userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }
    }, [
        userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        loadingUserEvents,
        userEventsMetaData
    ]);

    useEffect(() => {
        if (!('id' in usersCurrentAppointment) && !('id' in usersCurrentEvent)) setCurrentEvent({});
        configEvents();
        configUserEvents();
    }, [usersCurrentAppointment, usersCurrentEvent]);

    useEffect(() => {
        if (!('id' in adviser)) return;
        if (
            !loadingModules &&
            !!courseModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in courseModulesMetaData &&
            !courseModulesMetaData.last
        ) {
            dispatch(
                searchCourseModulesByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    courseModulesMetaData.number + 1
                )
            );
        } else if (courseModulesMetaData.last)
            setCurrentModules(
                courseModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
    }, [
        courseModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        courseModulesMetaData,
        loadingModules
    ]);

    useEffect(() => {
        configEvents();
    }, [currentAppointments, participants, appointmentsCreated[adviser.id]]);

    useEffect(() => {
        configUserEvents();
    }, [currentUserEvents, userEventsCreated[adviser.id]]);

    useEffect(() => {
        configBackgroundEvents();
    }, [currentModules]);

    useEffect(() => {
        configAppointments();
    }, [appointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]]);

    useEffect(() => {
        if (!Object.keys(currentEvent).length) return;
        if (currentEvent?.participantEntry && 'status' in currentEvent.participantEntry) {
            const isClosed = currentEvent.participantEntry.status === 'Closed';
            const restrictRoles = isClosed && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setRestrictOnClosed(restrictRoles);
        }
    }, [currentEvent]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === currentParticipantEntry?.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        ) {
            if (navLink === 'participant') navigate('/edit_participant');
            if (navLink === 'email') {
                navigate('/email_communication', {
                    state: {
                        status: usersCurrentAppointment.attendances[0]?.status,
                        appointmentId: usersCurrentAppointment.id
                    }
                });
            }
            if (navLink === 'letter') {
                navigate('/letter_communication', {
                    state: {
                        status: usersCurrentAppointment.attendances[0]?.status,
                        appointmentId: usersCurrentAppointment.id
                    }
                });
            }
        }
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant, navLink]);

    // HELPER FNS
    const fetchNewData = (adviserId) => {
        if (!adviserId) return;
        const params = {
            userIds: [adviserId],
            dateFrom: range.dateFrom,
            dateTo: range.dateTo
        };
        if (!(`${adviserId}_${range.dateFrom}_${range.dateTo}` in appointments))
            dispatch(searchAppointmentsAndSlotsByUserAndDateRange(params, 0));
        else
            setCurrentAppointments(
                appointments[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );

        if (!(`${adviserId}_${range.dateFrom}_${range.dateTo}` in userEventsByUser))
            dispatch(searchUserEventsByUserAndDateRange(params, 0));
        else
            setCurrentUserEvents(
                userEventsByUser[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );

        if (!(`${adviserId}_${range.dateFrom}_${range.dateTo}` in courseModules))
            dispatch(searchCourseModulesByUserAndDateRange(params, 0));
        else
            setCurrentModules(
                courseModules[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );
    };

    const configAppointments = () => {
        if (!('id' in adviser)) return;
        setCurrentAppointments(
            appointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
        );
    };

    const clearCurrentEventData = () => {
        setCurrentAppointments([]);
        setCurrentUserEvents([]);
        setCurrentModules([]);
        setCurrentEvent({});
        setEventOwnerId({});
    };

    const cleanUp = () => {
        dispatch(clearAppointmentsCreated());
        dispatch(clearUserEventsCreated());
        dispatch(clearAppointmentsAndSlotsByUserAndDateRange());
        dispatch(clearUserEventsByUserAndDateRange());
        dispatch(clearCourseModulesByUserAndDateRange());
        dispatch(clearParticipantsSearch());
        dispatch(clearUsersBySearch());
    };

    // EVENT HANDLERS
    const onDragEditTimes = (time, update) => {
        if (update) {
            const { date, startTime, endTime } = formatFullCalendarTimes(time);
            if (currentEvent.eventType === 'userEvent') {
                const updatedUserEvent = { ...usersCurrentEvent, date, startTime, endTime };
                dispatch(
                    updateUserEvent(
                        usersCurrentEvent.id,
                        usersCurrentEvent.userEventType,
                        updatedUserEvent
                    )
                );
            } else {
                const updatedAppointment = { ...usersCurrentAppointment, date, startTime, endTime };
                const params = [eventOwnerId, currentEvent.appointmentType, updatedAppointment];
                if (currentEvent.slots) dispatch(updateUserCalendarAppointmentSlot(...params));
                else dispatch(updateUserCalendarAppointment(...params));
            }
        }
        dispatch(setUsersCurrentAppointment({}));
        dispatch(setCurrentUserEvent({}));
    };

    const onTogglePanel = (open) => dispatch(setOpenAppointmentSchedulerPanel(open));

    const onSelectEvent = (id) => {
        setNewEvent({});
        if (id === currentEvent.id) {
            userClickCount++;
            const timeoutId = setTimeout(() => (userClickCount = 0), _DOUBLE_CLICK);
            if (userClickCount > 1) dispatch(setOpenAppointmentSchedulerPanel(true));
            return () => clearTimeout(timeoutId);
        } else if (id !== usersCurrentAppointment?.id || id !== usersCurrentEvent?.id) {
            const newAppointments =
                adviser.id in appointmentsCreated ? appointmentsCreated[adviser.id] : [];
            const selectedAppointment = [...currentAppointments, ...newAppointments].find(
                (el) => el.id === id
            );
            if (selectedAppointment) {
                dispatch(setUsersCurrentAppointment(selectedAppointment));
                dispatch(setCurrentUserEvent({}));
                setEventOwnerId(adviser.id);
            } else {
                const newUserEvents =
                    adviser.id in userEventsCreated ? userEventsCreated[adviser.id] : [];
                const selectedEvent = [...currentUserEvents, ...newUserEvents].find(
                    (el) => el.id === id
                );
                if (selectedEvent) {
                    dispatch(setUsersCurrentAppointment({}));
                    dispatch(setCurrentUserEvent(selectedEvent));
                    setEventOwnerId(loggedInUser.id);
                }
            }
        }
    };

    const onSelectSlot = (start, end) => {
        dispatch(setUsersCurrentAppointment({}));
        dispatch(setCurrentUserEvent({}));
        const { date, startTime, endTime } = formatFullCalendarTimes({ start, end });
        setNewEvent({ date, startTime, endTime });
        onTogglePanel(true);
    };

    const onGetCurrentTimeSpan = (dateFrom, dateTo, view) => {
        setRange({
            dateFrom,
            dateTo: view === 'timeGridDay' ? dateFrom : dateTo
        });
    };

    const onAdviserChange = (chosenId) => {
        if (!chosenId) {
            if (adviser.id !== loggedInUser.id) setAdviser(loggedInAdviser);
        } else if (chosenId !== adviser.id) {
            clearCurrentEventData();
            const chosenAdviser = [...users, loggedInAdviser].find((el) => el.id === chosenId);
            if (chosenAdviser) {
                setAdviser({
                    ...chosenAdviser,
                    contractIds: chosenAdviser.contracts || chosenAdviser.contractIds,
                    serviceIds: getServiceIds(chosenAdviser)
                });
            }
        }
    };

    const onNav = (event, link) => {
        const { extendedProps = {} } = event || {};
        dispatch(setCurrentlySelectedParticipant(extendedProps.participantEntry));
        if (link === 'letter' || link === 'email') {
            if (event._def.publicId !== usersCurrentAppointment?.id) {
                const newAppointments =
                    adviser.id in appointmentsCreated ? appointmentsCreated[adviser.id] : [];
                const selectedAppointment = [...currentAppointments, ...newAppointments].find(
                    (el) => el.id === event._def.publicId
                );
                selectedAppointment && dispatch(setUsersCurrentAppointment(selectedAppointment));
                setEventOwnerId(adviser.id);
            }
        }
        const { contractId = '' } = extendedProps.participantEntry;
        if (!(contractId in contractDetailsById))
            dispatch(loadContractDetailsForContractId(contractId));
        if (!(contractId in servicesByContractId))
            dispatch(loadServiceDetailsForContractId(contractId));
        setCurrentParticipantEntry(extendedProps.participantEntry);
        setIsPTRequestSent(true);
        setNavLink(link);
    };

    // RENDER
    return (
        <div className={classes.container}>
            <div className="calendar-wrapper">
                <AdviserSelect
                    userId={adviser.id}
                    serviceIds={loggedInUsersServiceIds}
                    initialAdviser={loggedInAdviser}
                    onAdviserChange={onAdviserChange}
                    disabled={isActionsDisabled}
                    customClass="calendarToolbarAdviserSelect"
                />

                <Calendar
                    type="userCalendar"
                    events={[...events, ...userEvents, ...courseEvents]}
                    currentEvent={currentEvent}
                    onDragEditTimes={onDragEditTimes}
                    onSelectSlot={onSelectSlot}
                    onSelectEvent={onSelectEvent}
                    isActionsDisabled={isActionsDisabled}
                    initialView="timeGridDay"
                    onGetCurrentTimeSpan={onGetCurrentTimeSpan}
                    onNav={onNav}
                />

                {openAppointmentSchedulerPanel &&
                    adviser.id &&
                    canCreate &&
                    !!Object.keys(newEvent)?.length && (
                        <UserAppointmentCreate
                            newAppointment={newEvent}
                            currentCalendarOwner={adviser}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser.id &&
                    currentEvent.eventType === 'participant' &&
                    currentEvent.slots &&
                    viewOnlySlots && (
                        <UserAppointmentSlotsView
                            usersCurrentAppointment={usersCurrentAppointment}
                            currentCalendarOwner={adviser}
                            currentLocation={currentEvent.location}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser.id &&
                    currentEvent.eventType === 'participant' &&
                    !currentEvent.slots &&
                    viewOnlyAppointment &&
                    currentEvent.participantEntry &&
                    !Object.keys(newEvent)?.length && (
                        <UserAppointmentView
                            usersCurrentAppointment={usersCurrentAppointment}
                            currentCalendarOwner={adviser}
                            currentLocation={currentEvent.location}
                            currentParticipantEntry={currentEvent.participantEntry}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser.id &&
                    currentEvent.eventType === 'participant' &&
                    currentEvent.slots &&
                    canEditSlots &&
                    !Object.keys(newEvent)?.length && (
                        <UserAppointmentSlotsEdit
                            usersCurrentAppointment={usersCurrentAppointment}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser.id &&
                    currentEvent.eventType === 'participant' &&
                    !currentEvent.slots &&
                    canEditAppointment &&
                    currentEvent.participantEntry &&
                    !Object.keys(newEvent)?.length && (
                        <UserAppointmentEdit
                            usersCurrentAppointment={usersCurrentAppointment}
                            currentCalendarOwner={adviser}
                            currentLocation={currentEvent.location}
                            currentParticipantEntry={currentEvent.participantEntry}
                        />
                    )}

                {openAppointmentSchedulerPanel &&
                    adviser.id === loggedInUser.id &&
                    currentEvent.eventType === 'userEvent' &&
                    !Object.keys(newEvent)?.length &&
                    canEditUserEvent && <UserEventEdit usersCurrentEvent={usersCurrentEvent} />}
            </div>
        </div>
    );
};

export default UserCalendar;
